import React, { useMemo, useState } from "react";
import { Button, Card, EmptyState, Input, Select } from "../../ui";
import { Mail, Trash2, UserCog } from "../../ui/icons";
import LudDashboardModal from "../../modals/LudDashboardModal";
import { DeleteModal } from "../../modals/Delete";
import PlatformLinksModal from "../../modals/PlatformLinksModal";
import "./Accounts.css";
import { ALL_INTEGRATIONS, integrationLabel, integrationOptions } from "../../../utils/integrations";
const emptyAccount = {
  id: "",
  app: "",
  accountId: "",
  email: "",
  secret: "",
  status: "inactive",
};
const emptyManager = { app: "", name: "", email: "", phone: "" };
export default function Accounts({
  lang,
  text,
  role,
  accounts,
  managers,
  platformLinks = [],
  onSaveAccount,
  onSavePlatformLinks,
  onSaveManager,
  onDeleteAccount,
  onDeleteManager,
  isAdmin,
}) {
  const [tab, setTab] = useState("accounts");
  const [modal, setModal] = useState(null);
  const [accountForm, setAccountForm] = useState(emptyAccount);
  const [managerForm, setManagerForm] = useState(emptyManager);
  const [platformLinksForm, setPlatformLinksForm] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const accountRows = useMemo(() => accounts, [accounts]);
  const openAccount = (account = emptyAccount) => {
    setAccountForm({ ...emptyAccount, ...account });
    setModal("account");
  };
  const openPlatformLinks = () => {
    const rows = platformLinks.length
      ? platformLinks.map((item) => ({
          app: item.app || "",
          url: item.url || "",
        }))
      : Array.from(
          new Set(accounts.map((account) => account.app).filter(Boolean)),
        ).map((app) => ({ app, url: "" }));
    setPlatformLinksForm(rows);
    setModal("platformLinks");
  };
  const updatePlatformLink = (index, key, value) => {
    setPlatformLinksForm((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item,
      ),
    );
  };
  const addPlatformLink = () => {
    setPlatformLinksForm((current) => [...current, { app: "", url: "" }]);
  };
  const submitPlatformLinks = async () => {
    await onSavePlatformLinks?.(
      platformLinksForm.filter((item) => item.app.trim()),
    );
    setModal(null);
  };
  const submitAccount = () => {
    onSaveAccount(accountForm);
    setModal(null);
  };
  const submitManager = () => {
    onSaveManager(managerForm);
    setManagerForm(emptyManager);
    setModal(null);
  };
  // A customer without any account has nothing to read here (only an administrator can add one): no empty card.
  if (!isAdmin && accountRows.length === 0) return null;
  const isAccountsTab = tab === "accounts";
  const isEmpty = isAccountsTab ? accountRows.length === 0 : !managers?.length;
  const emptyCopy = isAccountsTab
    ? {
        title: lang === "ar" ? "لا توجد حسابات تطبيقات" : "No application accounts",
        text: lang === "ar" ? "لا توجد حسابات تطبيقات متاحة حاليًا." : "No application accounts available.",
      }
    : {
        title: lang === "ar" ? "لا يوجد مديرو تطبيقات" : "No application managers",
        text: lang === "ar" ? "لا يوجد مديرو تطبيقات متاحون حاليًا." : "No application managers available.",
      };
  return (
    <section className="lud-dashboard-section">
      <Card className="lud-accounts-card">
        <div className="lud-accounts-head">
          <div className="lud-accounts-tabs" role="tablist">
            <button
              className={tab === "accounts" ? "active" : ""}
              onClick={() => setTab("accounts")}
            >
              {text.accounts}
            </button>
            <button
              className={tab === "managers" ? "active" : ""}
              onClick={() => setTab("managers")}
            >
              {text.managers}
            </button>
          </div>

          <div className="lud-row-actions">
            {tab === "accounts" && role === "admin" && (
              <Button size="sm" appearance="soft" onClick={openPlatformLinks}>
                {lang === "ar"
                  ? "إدارة روابط المنصات"
                  : "Manage Platform Links"}
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => {
                if (tab === "accounts") openAccount();
                else setModal("manager");
              }}
            >
              {tab === "accounts" ? text.addAccount : text.addManager}
            </Button>
          </div>
        </div>
        {!isEmpty ? <div className="lud-table-scroll">
          {tab === "accounts" ? (
            <table className="lud-dashboard-table">
              <thead>
                <tr>
                  <th>{lang === "ar" ? "التطبيق" : "Application"}</th>
                  <th>{lang === "ar" ? "المعرف" : "ID"}</th>
                  <th>{lang === "ar" ? "الإيميل" : "Email"}</th>
                  <th>{lang === "ar" ? "الرقم السري" : "Password"}</th>
                  <th>{text.platformLink}</th>
                  <th>{text.status}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {accountRows.map((account) => (
                  <tr key={account.id}>
                    <td>{integrationLabel(account.app, lang)}</td>
                    <td>{account.accountId || "—"}</td>
                    <td>{account.email || "—"}</td>
                    <td>{account.secret || "—"}</td>
                    <td>
                      {account.platformUrl ? (
                        <a
                          href={account.platformUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {text.platformLink}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <span className={`lud-status is-${account.status}`}>
                        {account.status === "active"
                          ? text.active
                          : account.status === "inProgress"
                            ? text.inProgress
                            : text.inactive}
                      </span>
                    </td>
                    <td>
                      <div className="lud-row-actions">
                        <Button
                          size="sm"
                          variant="primary"
                          appearance="filled"
                          onClick={() => openAccount(account)}
                        >
                          {text.edit}
                        </Button>
                        {isAdmin&&<Button
                          size="sm"
                          variant="danger"
                          appearance="ghost"
                          icon={<Trash2 size={16} />}
                          title={lang === "ar" ? "حذف" : "Delete"}
                          onClick={() =>
                            setDeleteTarget({ type: "account", id: account.id })
                          }
                        />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="lud-dashboard-table">
              <thead>
                <tr>
                  <th>{lang === "ar" ? "اسم التطبيق" : "Application"}</th>
                  <th>{lang === "ar" ? "اسم الشخص" : "Name"}</th>
                  <th>{lang === "ar" ? "الإيميل" : "Email"}</th>
                  <th>{lang === "ar" ? "رقم التواصل" : "Phone"}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {managers.map((manager) => (
                  <tr key={manager.id}>
                    <td>{integrationLabel(manager.app, lang)}</td>
                    <td>{manager.name}</td>
                    <td>{manager.email}</td>
                    <td>{manager.phone}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="danger"
                        appearance="ghost"
                        icon={<Trash2 size={16} />}
                        title={lang === "ar" ? "حذف" : "Delete"}
                        onClick={() =>
                          setDeleteTarget({ type: "manager", id: manager.id })
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div> : (
          <EmptyState
            className="lud-accounts-empty"
            icon={isAccountsTab ? <Mail size={26} /> : <UserCog size={26} />}
            title={emptyCopy.title}
            action={
              <Button
                size="sm"
                appearance="soft"
                onClick={() => (isAccountsTab ? openAccount() : setModal("manager"))}
              >
                {isAccountsTab ? text.addAccount : text.addManager}
              </Button>
            }
          >
            {emptyCopy.text}
          </EmptyState>
        )}
      </Card>

      <LudDashboardModal
        open={modal === "account"}
        title={accountForm.id ? text.edit : text.addAccount}
        onClose={() => setModal(null)}
        footer={
          <>
            <Button
              variant="primary"
              appearance="filled"
              onClick={submitAccount}
            >
              {text.save}
            </Button>
            <Button
              variant="danger"
              appearance="soft"
              onClick={() => setModal(null)}
            >
              {text.cancel}
            </Button>
          </>
        }
      >
        <div className="lud-form-grid">
          <Select
            label={lang === "ar" ? "التطبيق" : "Application"}
            placeholder={lang === "ar" ? "اختر التطبيق" : "Select app"}
            options={integrationOptions(ALL_INTEGRATIONS, lang)}
            value={accountForm.app}
            onChange={(event) =>
              setAccountForm((prev) => ({ ...prev, app: event.target.value }))
            }
          />
          <Input
            label={lang === "ar" ? "المعرف" : "ID"}
            value={accountForm.accountId}
            onChange={(event) =>
              setAccountForm((prev) => ({
                ...prev,
                accountId: event.target.value,
              }))
            }
          />
          <Input
            label={lang === "ar" ? "الإيميل" : "Email"}
            value={accountForm.email}
            onChange={(event) =>
              setAccountForm((prev) => ({ ...prev, email: event.target.value }))
            }
          />
          <Input
            label={lang === "ar" ? "الرقم السري" : "Password"}
            value={accountForm.secret}
            onChange={(event) =>
              setAccountForm((prev) => ({
                ...prev,
                secret: event.target.value,
              }))
            }
          />
          <Select
            label={text.status}
            value={accountForm.status}
            onChange={(event) =>
              setAccountForm((prev) => ({
                ...prev,
                status: event.target.value,
              }))
            }
            options={[
              { value: "active", label: text.active },
              { value: "inProgress", label: text.inProgress },
              { value: "inactive", label: text.inactive },
            ]}
          />
        </div>
      </LudDashboardModal>

      <PlatformLinksModal
        open={modal === "platformLinks"}
        lang={lang}
        text={text}
        links={platformLinksForm}
        onChange={updatePlatformLink}
        onAdd={addPlatformLink}
        onClose={() => setModal(null)}
        onSave={submitPlatformLinks}
      />

      <LudDashboardModal
        open={modal === "manager"}
        title={text.addManager}
        onClose={() => setModal(null)}
        footer={
          <>
            <Button
              variant="primary"
              appearance="filled"
              onClick={submitManager}
            >
              {text.save}
            </Button>
            <Button
              variant="danger"
              appearance="soft"
              onClick={() => setModal(null)}
            >
              {text.cancel}
            </Button>
          </>
        }
      >
        <div className="lud-form-grid">
          <Input
            label={lang === "ar" ? "اسم التطبيق" : "Application"}
            value={managerForm.app}
            onChange={(event) =>
              setManagerForm((prev) => ({ ...prev, app: event.target.value }))
            }
          />
          <Input
            label={lang === "ar" ? "اسم الشخص" : "Name"}
            value={managerForm.name}
            onChange={(event) =>
              setManagerForm((prev) => ({ ...prev, name: event.target.value }))
            }
          />
          <Input
            label={lang === "ar" ? "الإيميل" : "Email"}
            value={managerForm.email}
            onChange={(event) =>
              setManagerForm((prev) => ({ ...prev, email: event.target.value }))
            }
          />
          <Input
            label={lang === "ar" ? "رقم التواصل" : "Phone"}
            value={managerForm.phone}
            onChange={(event) =>
              setManagerForm((prev) => ({ ...prev, phone: event.target.value }))
            }
          />
        </div>
      </LudDashboardModal>

      {deleteTarget && (
        <DeleteModal
          id={deleteTarget.id}
          handleDelete={(id) => {
            if (deleteTarget.type === "account") {
              onDeleteAccount?.(id);
            } else {
              onDeleteManager?.(id);
            }
            setDeleteTarget(null);
          }}
          cancel={setDeleteTarget}
        />
      )}
    </section>
  );
}
